import { useLazyQuery } from '@apollo/client';

import { generateReceipt, GeneratorParams, ReceiptItem } from '@baemingo/receipt';
import { DateTime } from 'luxon';
import _ from 'lodash';

import { USER_QUERY } from './constants';
import { generateTemplate } from './utils';
import { buildReceiptDocument } from './document';
import { Payment, ReceiptType, Vars } from './types';
import { GetUser, GetUserVariables } from './__queries__';
import { useStorage } from '~Components/Storage';
import {
  nonLegacyCurrencies,
  useDeviceInfo,
  useDispatch,
  useI18n,
  useLogger,
  useMetadata,
  useSelector,
} from '~Hooks';
import { CardDetails, generateID, user } from '~reducers';
import { MODEL_WIDTHS, DEFAULT_WIDTH } from '~Components/PrinterManager';
import {
  ADYEN_PAPER_WIDTH_DOTS,
  ADYEN_RECEIPT_WIDTH,
} from '~Components/PrinterManager/AdyenReceipt';
import * as actions from '~actions';

interface Props {
  items: ReceiptItem[];
  totalDiscount: number;
  payments?: Payment[];
  cardDetails?: CardDetails;
  type: ReceiptType;
  extraVars: Vars;
  isTable?: boolean;
  easyId?: string;
  isCopy?: boolean;
  userId?: string;
}

export const useReceiptGenerator = ({ venueId }: { venueId: string }) => {
  const logger = useLogger('receipt-generator');
  const dispatch = useDispatch();
  const [storage] = useStorage();
  const deviceInfo = useDeviceInfo();
  const metadata = useMetadata();

  const appVersion = useSelector((state) => state.appVersion);
  const POSPrint = useSelector((state) => state.POSReceiptPrints);
  const staff = useSelector((state) => state.staff);
  const country = useSelector((state) => state.country);
  const companyName = useSelector((state) => state.companyName);
  const printers = useSelector((state) => state.printers);
  const primaryPrinter = printers.find((p) => p.primary);

  const isAdyenHandheldTerminal = deviceInfo?.isAdyenTerminal;
  const isZettleTerminal = deviceInfo?.isZettleTerminal;

  // If we should print from terminal and terminal has support for printing.
  const currentTerminal = isAdyenHandheldTerminal
    ? storage.adyenTerminals[0] // We only have one terminal so we can just pick the first one
    : storage.adyenTerminals.find((t) => t.POIID === POSPrint?.terminal); // This is when the device is and Ipad and has adyen terminals connected to it

  const [fetchUser] = useLazyQuery<GetUser, GetUserVariables>(USER_QUERY, {
    fetchPolicy: 'network-only',
  });

  /** Spain need user data for receipts, retry 4 times before we give up, dispatch a toast when starting to show user what we're are doing */
  const fetchUserWithRetry = async (userId: string, retries = 4) => {
    const toastId = generateID();

    dispatch(actions.addToast('Fetching user data for receipt...', false, toastId, false));

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        logger.info('Fetching user data for Spain receipt', { userId, attempt });
        const result = await fetchUser({
          variables: { userId },
        });

        if (result.data?.User) {
          logger.info('Successfully fetched user data for Spain receipt', {
            userId,
            attempt,
            userData: result.data.User,
          });

          /* Remove it in code if success */
          dispatch(actions.removeToast(toastId));
          return result.data.User;
        }

        logger.warn('User data not found in response', { userId, attempt });

        if (attempt < retries) {
          dispatch(
            actions.addToast(
              `Failed to fetch user data, retrying ${attempt} of ${retries - 1}...`,
              false,
              toastId,
              false,
            ),
          );
        }
      } catch (error) {
        logger.error('Failed to fetch user data for Spain receipt', {
          userId,
          attempt,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        if (attempt === retries) {
          logger.error('Used all retry attempts for user data fetch', {
            userId,
            totalAttempts: retries,
          });

          dispatch(
            actions.addToast(
              'Failed to fetch user data after multiple attempts',
              true,
              toastId,
              true,
            ),
          );
        } else {
          /* Show retry message for non-final attempts */
          dispatch(
            actions.addToast(
              `Failed to fetch user data, retrying ${attempt} of ${retries - 1}...`,
              false,
              toastId,
              false,
            ),
          );
        }
      }
    }

    return null;
  };

  const currency =
    country && nonLegacyCurrencies[country] ? nonLegacyCurrencies[country] : storage.currency.code;

  const [storageData] = useStorage();

  const { i18n } = useI18n(country === 'NO' ? 'no' : undefined);

  return async ({
    items,
    totalDiscount,
    payments,
    cardDetails,
    type,
    extraVars,
    isTable,
    easyId = '',
    isCopy,
    userId,
  }: Props) => {
    if (country === 'ES' && userId) {
      const userData = await fetchUserWithRetry(userId);

      if (userData) {
        Object.assign(extraVars, {
          ...(userData.name && { userName: userData.name }),
          ...(userData.email && { userEmail: userData.email }),
          ...(userData.phone && { userPhone: userData.phone }),
          ...(userData.address && { userAddress: userData.address }),
          ...(userData.city && { userCity: userData.city }),
          ...(userData.zip && { userZip: userData.zip }),
          ...(userData.orgNumber && { userOrgNumber: userData.orgNumber }),
        });
      }
    }

    // For Norway refund receipts, all sums should be prefixed with "-"
    const isNorwayRefund = country === 'NO' && type === ReceiptType.Refund;

    const maxPad = payments ? Math.max(...payments.map((payment) => payment.type.length)) : 0;
    const paymentVars = payments
      ? {
          ...payments.reduce(
            (acc, { type }, index) => ({ ...acc, [`paymentType${index}`]: type }),
            {},
          ),
          ...payments.reduce(
            (acc, { amount }, index) => ({
              ...acc,
              [`paymentAmount${index}`]: `${isNorwayRefund ? '-' : ''}${amount}`.padEnd(
                maxPad,
                ' ',
              ),
            }),
            {},
          ),
        }
      : [];

    const vars = {
      venueName: metadata.data.venueName,
      companyName: companyName,
      address: metadata.data.street,
      postalCode: metadata.data.zip,
      city: metadata.data.city,
      currency,
      orgNumber: metadata.data.organizationId,
      easyId,
      totalDiscount:
        (isNorwayRefund ? -Math.abs(totalDiscount) / 100 : totalDiscount / 100).toFixed(2) ?? '0',
      ...paymentVars,
      ...extraVars,
      datetime: DateTime.fromJSDate(
        extraVars?.datetime ? new Date(extraVars.datetime) : new Date(),
      ).toFormat('yyyy-MM-dd HH:mm'),
    };

    /** Filter out all items with 0 in amount if the setting is enabled */
    const filteredItems = storageData.hideZeroAmountItemsInCustomerPrints
      ? items.filter((item) => item.amount !== 0)
      : items;

    // For Norway refund receipts, negate item amounts and prefix quantity with "-"
    const receiptItems = isNorwayRefund
      ? filteredItems.map((item) => ({
          ...item,
          fraction: item.fraction ? -Math.abs(item.fraction) : -1,
          amount: -Math.abs(item.amount),
          discount: -Math.abs(item.discount),
        }))
      : filteredItems;

    const templateInput = {
      type,
      vars,
      isTable,
      payments,
      cardDetails,
      receiptTemplate: metadata.data.receiptTemplate,
      isTypeInvoice: type === ReceiptType.Invoice,
      items: receiptItems,
      i18n,
      isCopy,
      country: String(country || 'SE'),
      showTipSuggestions: storageData.showTipSuggestions,
      showStaffName: storageData.receiptShowStaffName,
      totalAmountLargeFont: storageData.receiptTotalAmountLargeFont,
      staff,
    };

    // Text template for fixed-width printers, structured document for image printers.
    // Both are derived from the same input so they always carry the same content.
    const template = generateTemplate(templateInput);
    const receiptDocument = buildReceiptDocument(templateInput);

    if (deviceInfo && deviceInfo?.systemName === 'Android') {
      MODEL_WIDTHS.mC_Print3 = 24;
    }

    // Adyen handheld terminals print 58mm paper (384 dots). The receipt is rasterised by
    // PrinterManager/AdyenReceipt, which lays out 32 standard or 16 large columns per line.
    const width =
      isAdyenHandheldTerminal && currentTerminal?.hasPrinter
        ? ADYEN_RECEIPT_WIDTH
        : MODEL_WIDTHS[primaryPrinter?.model || ''] ?? DEFAULT_WIDTH;

    const params: GeneratorParams = {
      items: receiptItems,
      width,
      truncateReceiptItemNames: storageData.truncateReceiptItemNames,
      vars,
      template,
    };

    const checkPrinterImageSupport = () => {
      // Adyen terminals with printers support image printing = This is the terminal used for printing receipts.
      if (isAdyenHandheldTerminal && currentTerminal?.hasPrinter) {
        return true;
      }

      // Support printing logo for zettle terminal when using the dock printer
      if (
        storageData.useHandheldPrinter &&
        isZettleTerminal &&
        primaryPrinter?.id === 'DockingStation'
      ) {
        return true;
      }

      // This is the old Star library that does not support image printing
      if (primaryPrinter?.model.startsWith('MCP')) {
        return false;
      }

      // If it is epson we support logo printing.
      if (primaryPrinter?.model.startsWith('TM-') || primaryPrinter?.model === 'EpsonSocket') {
        return true;
      }

      // Note: we don't support logo printing for star cloud printers.
      const isSupportedStarPrinter =
        primaryPrinter?.model.startsWith('mC_') ||
        primaryPrinter?.model.toLowerCase() === 'stario' ||
        primaryPrinter?.model.toLowerCase() === 'starprint';

      // If the app version is 6.0.0 or higher and it is a supported Star printer
      if (appVersion >= '6.0.0' && isSupportedStarPrinter) {
        return true;
      }

      // Fallback to not supporting logo printing
      return false;
    };

    // Convert receipt template image to base64 for logo printing (only if printer supports images)
    let base64Image: string | undefined;
    const logoUrl = metadata.data.receiptTemplate?.image?.file?.url;
    const printerSupportsImages = checkPrinterImageSupport();

    if (logoUrl && printerSupportsImages) {
      try {
        base64Image = await new Promise<string>((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            // Adyen terminals support max 384px wide images, other printers use 570px
            const isAdyenPrint = isAdyenHandheldTerminal && !!currentTerminal?.hasPrinter;
            const paperWidth = isAdyenPrint ? ADYEN_PAPER_WIDTH_DOTS : 570;
            const maxLogoWidth = paperWidth * 0.8;
            const maxLogoHeight = 200;

            // Scale down to fit within max width and max height, preserving aspect ratio
            const scale = Math.min(1, maxLogoWidth / img.width, maxLogoHeight / img.height);
            const logoWidth = Math.round(img.width * scale);
            const logoHeight = Math.round(img.height * scale);

            // Add white space below logo for non-Adyen printers (the Adyen renderer adds its own gap)
            const bottomPadding = isAdyenPrint ? 0 : 40;

            const canvas = document.createElement('canvas');
            canvas.width = paperWidth;
            canvas.height = logoHeight + bottomPadding;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Failed to get canvas context'));
              return;
            }

            // White background with logo centered
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, paperWidth, logoHeight + bottomPadding);
            const x = Math.round((paperWidth - logoWidth) / 2);
            ctx.drawImage(img, x, 0, logoWidth, logoHeight);

            const dataUrl = canvas.toDataURL('image/png');
            resolve(dataUrl);
          };
          img.onerror = () => reject(new Error('Failed to load receipt logo image'));
          img.src = logoUrl;
        });
      } catch (error) {
        logger.warn('Failed to convert receipt logo to base64, printing without logo', {
          errorData: error,
        });
      }
    }

    return {
      base64Image,
      lines: generateReceipt(params),
      document: receiptDocument,
    };
  };
};
